// frontend/src/components/fulfillment/FulfillmentDashboard.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';

import ViewHeader from '../ViewHeader';
import PageContainer from '../common/PageContainer';
import FulfillmentKPIDashboard from './FulfillmentKPIDashboard';
import FulfillmentTables from './FulfillmentTables';
import { ConfirmDialog } from '../common/confirm';
import RangeToggle from '../common/RangeToggle';
import useListQuery from '../common/listQuery/useListQuery';
import { SP } from '../../theme/tokens';
import { toast } from 'sonner';

const API = process.env.REACT_APP_API_BASE_URL || '';

const eurSafe = (n) => (Number.isFinite(n) ? n : 0);
const sumWarehouse = (r) =>
  (r.picking_net || 0) + (r.packing_net || 0) + (r.loading_net || 0) +
  (r.materials_net || 0) + (r.fees_net || 0) + (r.returns_net || 0) +
  (r.storage_net || 0) + (r.other_net || 0);

// 🔴 W-41: `warehouse` und `transport` fehlten hier — und genau deshalb
// mussten die beiden Cost-Kacheln eine fremde Reihe zeichnen. Die Zerlegung
// steckt in jeder Zeile bereits drin (`sumWarehouse`, `transport_net`); sie
// wurde nur nie JE MONAT aufsummiert.
const groupByMonthAgg = (rows) =>
  rows.reduce((acc, r) => {
    const m = r.month || 'unknown';
    if (!acc[m]) acc[m] = { total: 0, dnCount: 0, rtCount: 0, warehouse: 0, transport: 0 };
    acc[m].total += r.total_net || 0;
    acc[m].warehouse += sumWarehouse(r);
    acc[m].transport += r.transport_net || 0;
    if (r.ref_kind === 'DN') acc[m].dnCount += 1;
    if (r.ref_kind === 'RT') acc[m].rtCount += 1;
    return acc;
  }, {});

/** Der Normalisierer der Filterwerte — dieselbe Form wie in der Tabelle. */
const normalize = (value, fallback) => (value != null && value !== '' ? value : fallback);

/** Die zwei Inhaltsbereiche (Achse `tab`, §3.1). */
const TABS = ['orders', 'allocation'];

/** Die sortierbaren Spalten — `-feld` heisst absteigend (C3-9). */
const SORT_KEYS = [
  'month',
  'reference_number',
  'order_number',
  'ref_kind',
  'product_count',
  'carrier',
  'order_country',
  'total_net',
];
// 🔴 BEIDE Richtungen stehen in `values`: `leseAchse` verwirft einen Wert, den
// die Liste nicht fuehrt — mit nur `total_net` faende `?sort=-carrier` keinen
// Treffer und fiele still auf die Vorgabe zurueck.
const SORT_VALUES = SORT_KEYS.flatMap((k) => [k, `-${k}`]);

/**
 * FulfillmentDashboard — die Schale des Fulfillment-Berichts.
 *
 * Sie haelt den **Zustand der Adresse** (`useListQuery`, §3.1) und die
 * Datenabrufe; die Flaechen darunter zeichnen. Seit UI2 Teil 10, G13a-4a:
 *
 *  · Reiter, Suche, Filter, Rahmen (`basis`), Sortierung und Seite liegen in
 *    der URL — ein Neuladen verliert nichts (N10.1/N10.2).
 *  · Der KPI-Umschalter ist aus der Kachel heraus und steht als
 *    `RangeToggle` UEBER der Reihe (C3-12) — die Kachel traegt keine Bedienung
 *    mehr (OE-1, N3.5).
 *  · Die Orders-Liste laedt bei **genau einem** Monat serverseitig
 *    (`?month=`, B1) und traegt `total` aus derselben Abfrage; bei keinem oder
 *    mehreren Monaten gilt die Kappung, und die Liste sagt es (C3-17).
 */
export default function FulfillmentDashboard() {
  // ----------------- Optionen (Server) -----------------
  const [months, setMonths] = useState([]);
  const [monthsLoaded, setMonthsLoaded] = useState(false);
  const [countries, setCountries] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [carrierOptions, setCarrierOptions] = useState([]);
  const [carrierLoading, setCarrierLoading] = useState(false);

  // ----------------- Daten -----------------
  const [orders, setOrders] = useState([]);
  // 🔴 `total` kommt vom Server und ist die Menge VOR der Kappung. `null`
  // heisst „der Server hat keine Zahl geliefert" — dann zeigt der Zaehler
  // keine „von"-Form, statt eine geratene Gesamtmenge zu behaupten.
  const [ordersTotal, setOrdersTotal] = useState(null);
  const [ordersReady, setOrdersReady] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState(false);
  // Der Wiederhol-Zaehler ist der EINE Weg, denselben Abruf erneut zu fahren.
  const [versuch, setVersuch] = useState(0);

  const [, setInbounds] = useState([]);
  const [, setReturnsRows] = useState([]);
  const [allocRows, setAllocRows] = useState([]);

  // ----------------- UI -----------------
  const [isRefreshing, setIsRefreshing] = useState(false);
  // 🔴 ERSTES BILD, NICHT JEDER ABRUF: `kpiOverall` startet mit Nullen, und
  // ohne diese Marke rendert der Streifen beim ersten Bild „€0 · 0 · 0".
  const [kpiLoading, setKpiLoading] = useState(true);
  const [allocLoading, setAllocLoading] = useState(false);
  const [rebuildAllocLoading, setRebuildAllocLoading] = useState(false);

  const [kpiOverall, setKpiOverall] = useState({
    totals: { shipments: 0, allKinds: 0 },
    shipmentsAll: 0,
    distinctCountries: 0,
    perCarrier: {},
    sparklineTotalShipments: [],
    sparklineTotalAllKinds: [],
    sparklineShipments: [],
    sparklineReturnRate: [],
    kindTotals: {},
  });
  // Unterscheidet „Abruf fehlgeschlagen" von „Wert ist wirklich null".
  const [overallError, setOverallError] = useState(false);

  const [kpiFiltered, setKpiFiltered] = useState({
    avgPrice: 0,
    shipments: 0,
    returns: 0,
    total: 0,
    warehouseTotal: 0,
    transportTotal: 0,
    sparklineTotal: [],
    sparklineShipments: [],
    sparklineReturnRate: [],
    sparklineWarehouse: [],
    sparklineTransport: [],
    byKind: {},
  });

  const [rollingKpis, setRollingKpis] = useState(null);
  const [rollingError, setRollingError] = useState(null);
  const [rollingLoading, setRollingLoading] = useState(true);

  // ----------------- Der Listen-Zustand (die Adresse) -----------------
  const monthValues = useMemo(() => months.slice(), [months]);
  // 🔴 Der neueste Monat ist die VORGABE der Monatsfacette — dynamisch, weil
  // die Liste erst mit der Antwort von `/months` feststeht. Der Chip traegt
  // „· default", solange er gilt (P28); `null` waere „alle Monate" (W10).
  const defaultMonths = useMemo(
    () => (months.length ? [months[months.length - 1]] : []),
    [months]
  );
  const carrierValues = useMemo(
    () => [...new Set(carrierOptions.map((c) => normalize(c, 'others')))],
    [carrierOptions]
  );
  const countryValues = useMemo(
    () => [...new Set(countries.map((c) => normalize(c, 'Unknown')))],
    [countries]
  );

  const dimensions = useMemo(
    () => [
      // Rahmen-Segment, KEIN Chip: `period` engt nicht ein (B3).
      { key: 'basis', kind: 'period', values: ['shipments', 'all'], default: 'shipments', label: 'Cost basis' },
      { key: 'q', kind: 'single', label: 'Search' },
      // 🔴 **`q_sku` ist hier NICHT deklariert** (Regel-15-W2 / Regel-16-W3 der
      // Runde 1). Plan C3-2 gibt die SKU-Suche dem ALLOCATION-Panel, und das
      // bekommt seine Leiste erst mit G13a-4b — mit dem zweiten Hook
      // (`useListQuery`) und dem Bedienelement. Solange keine Flaeche den Wert
      // liest, erzeugte eine Deklaration hier nur einen wirkungslosen Chip
      // „SKU: …" im Orders-Panel, den „Clear all" nicht einmal raeumte. Ein
      // alter Link mit `?q_sku=…` bleibt in der Adresse stehen (der Hook
      // verwirft unbekannte SCHLUESSEL nicht) und wird mit C3b lebendig.
      { key: 'months', kind: 'multi', values: monthValues, default: defaultMonths, label: 'Months' },
      { key: 'carriers', kind: 'multi', values: carrierValues, label: 'Carriers' },
      { key: 'country', kind: 'multi', values: countryValues, label: 'Country' },
      // `quick` traegt EINEN Chip je gedruecktem Wert (§3.3 N3.3).
      {
        key: 'kind',
        kind: 'quick',
        values: [
          { value: 'DN', label: 'Shipments' },
          { value: 'RT', label: 'Returns' },
          { value: 'IN', label: 'Inbounds' },
          { value: 'TO', label: 'Transfers' },
          { value: 'STORAGE', label: 'Storage' },
        ],
        label: 'Kind',
      },
    ],
    [monthValues, defaultMonths, carrierValues, countryValues]
  );

  const query = useListQuery({
    dimensions,
    sort: { default: '-total_net', values: SORT_VALUES },
    tabs: TABS,
    pageSize: 50,
  });
  const { state, set } = query;

  const basis = state.basis === 'all' ? 'all' : 'shipments';
  const monate = Array.isArray(state.months) ? state.months : [];
  // 🔴 Der Monat ist bei GENAU EINEM Wert ein Serverfilter (B1). Bei keinem
  // oder mehreren laedt der Server ungefiltert (und kappt), der Clientfilter
  // in der Tabelle blendet die uebrigen Monate aus.
  const serverMonth = monate.length === 1 ? monate[0] : null;

  // ----------------- Der Orders-Abruf -----------------
  // 🔴 Er haengt am SERVERMONAT, nicht an jedem Filter: `carriers`, `country`
  // und `kind` wirken auf den geladenen Zeilen und loesen keinen zweiten Abruf
  // aus (C3-17a). Und er wartet auf den Monats-Default — sonst laedt die Seite
  // erst 2 000 ungefilterte Zeilen und dann den Monat (C3-17b).
  useEffect(() => {
    if (!monthsLoaded) return undefined;
    const steuerung = new AbortController();
    let abgemeldet = false;
    setOrdersLoading(true);
    const url = `${API}/api/fulfillment/orders${
      serverMonth ? `?month=${encodeURIComponent(serverMonth)}` : ''
    }`;
    fetch(url, { signal: steuerung.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`orders ${res.status}`);
        return res.json();
      })
      .then((payload) => {
        if (abgemeldet) return;
        setOrders(Array.isArray(payload.rows) ? payload.rows : []);
        setOrdersTotal(Number.isFinite(payload.total) ? payload.total : null);
        setOrdersReady(true);
        setOrdersError(false);
      })
      .catch((err) => {
        if (abgemeldet || steuerung.signal.aborted) return;
        console.error('[fulfillment] /orders failed', err);
        setOrdersError(true);
      })
      .finally(() => {
        if (!abgemeldet) setOrdersLoading(false);
      });
    return () => {
      abgemeldet = true;
      steuerung.abort();
    };
  }, [monthsLoaded, serverMonth, versuch]);

  const wiederholen = useCallback(() => setVersuch((v) => v + 1), []);

  // ----------------- Optionen laden -----------------
  useEffect(() => {
    const loadCountries = async () => {
      setCountriesLoading(true);
      try {
        const qs = new URLSearchParams();
        if (monate.length === 1) qs.set('month', monate[0]);
        const res = await fetch(`${API}/api/fulfillment/countries?${qs.toString()}`);
        const data = await res.json();
        setCountries(Array.isArray(data) ? data : []);
      } catch {
        setCountries([]);
      } finally {
        setCountriesLoading(false);
      }
    };
    loadCountries();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nur der EINE Monat ist ein Serverparameter
  }, [serverMonth]);

  const refreshAllocation = useCallback(async () => {
    try {
      setAllocLoading(true);
      const payload = { include_storage: true };
      const res = await fetch(`${API}/api/fulfillment/allocation/by-sku`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      setAllocRows(data.rows || []);
    } finally {
      setAllocLoading(false);
    }
  }, []);

  // ----------------- Alles auffrischen -----------------
  const refreshAllData = async () => {
    setIsRefreshing(true);
    // 🔴 ZUERST ZURUECKSETZEN: ein einmal aufgetretener Fehler blieb frueher
    // fuer immer stehen, und ein gelungener Neuversuch raeumte ihn nicht weg.
    setOverallError(false);
    setOrdersError(false);
    setVersuch((v) => v + 1);

    const refreshMonths = async () => {
      const r = await fetch(`${API}/api/fulfillment/months`);
      // 🔴 OHNE diese Pruefung wirft `r.json()` bei einer 500-HTML-Antwort —
      // der Fluss springt in den aeusseren `catch`, `fetchRolling` laeuft nie,
      // und beide Cost-Kacheln haengen dauerhaft im Skelett.
      if (!r.ok) throw new Error(`months ${r.status}`);
      const j = await r.json();
      const sorted = Array.isArray(j.months) ? [...j.months].sort() : [];
      setMonths(sorted);
      setMonthsLoaded(true);
      return sorted;
    };

    const refreshCarrierOptions = async () => {
      try {
        setCarrierLoading(true);
        const r = await fetch(`${API}/api/fulfillment/carriers`);
        if (r.ok) {
          const arr = await r.json();
          setCarrierOptions(arr || []);
          return;
        }
      } catch {
        /* fall through */
      } finally {
        setCarrierLoading(false);
      }
      // Rueckfall aus `/orders` — dieselbe Quelle, nur ohne eigenen Endpunkt.
      try {
        setCarrierLoading(true);
        const r2 = await fetch(`${API}/api/fulfillment/orders`);
        const j2 = await r2.json();
        const rows = j2.rows || [];
        const s = new Set(
          rows.filter((rr) => rr.ref_kind === 'DN').map((rr) => rr.carrier || 'others').filter(Boolean)
        );
        setCarrierOptions([...s].sort());
      } catch {
        setCarrierOptions([]);
      } finally {
        setCarrierLoading(false);
      }
    };

    const refreshOverallKPIs = async () => {
      try {
        const res = await fetch(`${API}/api/fulfillment/orders/aggregate`);
        if (!res.ok) throw new Error(`aggregate ${res.status}`);
        const j = await res.json();

        const totals = { allKinds: eurSafe(j.total_all_kinds), shipments: eurSafe(j.shipments_total) };
        const perCarrier = {};
        (j.per_carrier || []).forEach((row) => { perCarrier[row.carrier] = row.shipments; });

        const kindTotals = {};
        (j.by_kind || []).forEach((k) => { kindTotals[k.ref_kind] = eurSafe(k.total); });

        const last6 = (j.by_month || []).slice(-6);
        const sparklineTotalAllKinds = last6.map((d) => ({ x: d.month, y: eurSafe(d.total_all_kinds) }));
        const sparklineTotalShipments = last6.map((d) => ({ x: d.month, y: eurSafe(d.shipments_total) }));
        const sparklineShipments = last6.map((d) => ({ x: d.month, y: d.dn_count || 0 }));
        const sparklineReturnRate = last6.map((d) => {
          const dn = d.dn_count || 0, rt = d.rt_count || 0;
          return { x: d.month, y: dn ? (rt / dn) * 100 : 0 };
        });

        setKpiOverall({
          totals,
          shipmentsAll: j.shipments_all || 0,
          distinctCountries: j.distinct_countries || 0,
          perCarrier,
          sparklineTotalAllKinds,
          sparklineTotalShipments,
          sparklineShipments,
          sparklineReturnRate,
          kindTotals,
        });
      } catch (e) {
        // 🔴 Der stille `/orders`-Fallback ist WEG (Konzept §2.2 F7): er
        // rechnete bei einem toten `/orders/aggregate` dieselben Kacheln aus
        // einem ungepagten Zeilensatz neu — andere Zahlen, dasselbe Label.
        console.error('[fulfillment] /orders/aggregate failed', e);
        throw e;
      }
    };

    // 🔴 `/countries/breakdown` wird NICHT mehr geholt (G13a-4a, Nachbesserung 1).
    // Der einzige Verbraucher war die Donut-Karte „Top countries" der KPI-Reihe,
    // und die ist am 2026-05-20 entfallen (`1eb15c8374a`: „Removed unused
    // DonutCard import + topCountries memo + donutData/donutTotal +
    // overallPerCountry prop destructure"). Danach hat der Elternteil den Wert
    // fuenf Monate lang geholt und an eine Prop gereicht, die das Kind nicht
    // mehr deklariert; mit der C3-Schale fiel auch die Prop-Weitergabe, und der
    // Zustand stand ohne Leser da. Der Abruf faellt mit weg — er kostete je
    // Auffrischen eine Anfrage, im Rueckfall sogar einen zweiten `/orders`-Abruf
    // (Backlog 2026-10-06, „Doppelladung"). Wer die Aufschluesselung wieder
    // zeigen will, holt sie mit einem Verbraucher zurueck, nicht vorher.

    const refreshRolling = async () => {
      setRollingLoading(true);
      try {
        const res = await fetch(`${API}/api/fulfillment/kpis/rolling`);
        if (!res.ok) throw new Error(`rolling ${res.status}`);
        const payload = await res.json();
        setRollingKpis(payload);
        setRollingError(null);
      } catch (err) {
        setRollingError(String(err?.message || err));
        setRollingKpis(null);
      } finally {
        setRollingLoading(false);
      }
    };

    // 🔴 Jede Quelle bekommt ihr EIGENES catch. `Promise.all` allein ist hier
    // die falsche Form: rejectet EINER der Aufrufe, verlaesst die Rejection den
    // Block — und die Quellen, die sauber geladen hatten, werden mitgerissen.
    const guarded = async (fn, onError) => {
      try {
        await fn();
      } catch (err) {
        console.error('[fulfillment] fetch failed', err);
        onError(err);
      }
    };

    try {
      await refreshMonths();
      await Promise.all([
        // `orders` laeuft ueber den Effekt oben — er haengt am Servermonat und
        // am Wiederhol-Zaehler, der hier bereits hochgezaehlt ist.
        guarded(refreshRolling, () => {}),
        guarded(refreshAllocation, () => {}),
        guarded(refreshOverallKPIs, () => setOverallError(true)),
        guarded(refreshCarrierOptions, () => {}),
      ]);
    } catch (e) {
      console.error('[fulfillment] refreshAllData failed', e);
      setOverallError(true);
      setOrdersError(true);
      setRollingError(String(e?.message || e));
      setRollingKpis(null);
    } finally {
      setIsRefreshing(false);
      setKpiLoading(false);
      setRollingLoading(false);
    }
  };

  const [rebuildConfirmOpen, setRebuildConfirmOpen] = useState(false);

  const handleRebuildAllocation = useCallback(async () => {
    if (rebuildAllocLoading) return;
    try {
      setRebuildAllocLoading(true);
      const res = await fetch(`${API}/api/fulfillment/allocation/rebuild`, { method: 'POST' });
      if (!res.ok) {
        const koerper = await res.text().catch(() => '');
        console.error('[fulfillment] allocation rebuild failed', res.status, koerper);
        throw new Error('Could not rebuild the allocation table.');
      }
      await refreshAllData();
      toast.success('Allocation table rebuilt.');
    } catch (err) {
      console.error(err);
      toast.error('Could not rebuild the allocation table. Check the log for details.');
    } finally {
      setRebuildAllocLoading(false);
      setRebuildConfirmOpen(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- refreshAllData not memoized; called via closure, stable enough
  }, [rebuildAllocLoading]);

  useEffect(() => {
    refreshAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Die beiden Quellen sind heute fuer keine Flaeche gelesen; sie laufen
  // weiterhin je Monatswechsel, damit die Naht nicht still verschwindet.
  useEffect(() => {
    if (!serverMonth) return;
    fetch(`${API}/api/fulfillment/inbounds?month=${serverMonth}`)
      .then((r) => r.json())
      .then((j) => setInbounds(j.rows || []))
      .catch(() => setInbounds([]));
    fetch(`${API}/api/fulfillment/returns?month=${serverMonth}`)
      .then((r) => r.json())
      .then((j) => setReturnsRows(j.rows || []))
      .catch(() => setReturnsRows([]));
  }, [serverMonth]);

  // recompute Filtered KPIs from current orders
  useEffect(() => {
    const rows = orders || [];
    const dn = rows.filter((x) => x.ref_kind === 'DN');
    const rt = rows.filter((x) => x.ref_kind === 'RT');

    const shipments = dn.length;
    const returns = rt.length;
    const totalDN = dn.reduce((s, r) => s + (r.total_net || 0), 0);
    const totalRT = rt.reduce((s, r) => s + (r.total_net || 0), 0);
    const total = totalDN + totalRT;

    const warehouseTotal = dn.reduce((s, r) => s + sumWarehouse(r), 0) + rt.reduce((s, r) => s + sumWarehouse(r), 0);
    const transportTotal = dn.reduce((s, r) => s + (r.transport_net || 0), 0) + rt.reduce((s, r) => s + (r.transport_net || 0), 0);
    const avgPrice = shipments > 0 ? totalDN / shipments : 0;

    const grouped = groupByMonthAgg(rows);
    const monthsSorted = Object.keys(grouped).filter((m) => m !== 'unknown').sort();
    const last = monthsSorted.slice(-6);

    setKpiFiltered({
      avgPrice,
      shipments,
      returns,
      total,
      warehouseTotal,
      transportTotal,
      sparklineTotal: last.map((m) => ({ x: m, y: grouped[m].total })),
      sparklineShipments: last.map((m) => ({ x: m, y: grouped[m].dnCount })),
      sparklineWarehouse: last.map((m) => ({ x: m, y: grouped[m].warehouse })),
      sparklineTransport: last.map((m) => ({ x: m, y: grouped[m].transport })),
      sparklineReturnRate: last.map((m) => {
        const d = grouped[m]; const a = d.dnCount || 0; const b = d.rtCount || 0;
        return { x: m, y: a ? (b / a) * 100 : 0 };
      }),
      byKind: rows.reduce((acc, r) => {
        acc[r.ref_kind] = (acc[r.ref_kind] || 0) + (r.total_net || 0);
        return acc;
      }, {}),
    });
  }, [orders]);

  return (
    <>
      <Box data-testid="liste-kopf">
        <ViewHeader
        breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'Fulfillment' }]}
        title="Fulfillment Dashboard"
        // 🔴 KM-1 (2026-10-10): **hier steht KEIN `SplitButton` mehr.** Diese
        // Seite hat einen ⋯ („Rebuild allocation table"), und ein Seitenkopf
        // zeigt höchstens EIN Menü. Der Kopf rendert „Scan duplicates" als
        // einfachen Knopf und stellt „Run data checks" als obersten
        // ⋯-Eintrag darüber.
        actions={[
          {
            key: 'scan',
            label: 'Scan duplicates',
            emphasis: 'primary',
            onClick: () => window.dispatchEvent(new CustomEvent('fulfill:scan')),
            menu: [{
              key: 'checks',
              label: 'Run data checks',
              onClick: () => window.dispatchEvent(new CustomEvent('fulfill:checks')),
            }],
          },
        ]}
        overflow={[
          {
            label: rebuildAllocLoading ? 'Rebuilding allocation…' : 'Rebuild allocation table',
            onClick: () => setRebuildConfirmOpen(true),
            disabled: rebuildAllocLoading,
          },
        ]}
        />
      </Box>

      <PageContainer>
        <>
          {/* 🔴 C3-12: das Rahmen-Segment steht UEBER der KPI-Reihe und
              AUSSERHALB von `liste-kpi` — `messeP29` klickt den ersten Knopf
              IN der KPI-Zone und verlangt, dass die Texte sich nicht aendern.
              `period`, nicht `single`: es engt keine Datensaetze ein und traegt
              deshalb keinen Chip in Z3 (B3). */}
          <Box sx={{ mb: SP.card }}>
            <RangeToggle
              label="Cost basis"
              checkActive
              options={[
                { value: 'shipments', label: 'Shipments' },
                { value: 'all', label: 'All kinds' },
              ]}
              value={basis}
              onChange={(next) => set('basis', next)}
            />
          </Box>

          {/* Zone 2 (`liste-kpi`) — P2/P3 lesen die Reihenfolge. */}
          <Box data-testid="liste-kpi">
          <FulfillmentKPIDashboard
            overallError={overallError}
            ordersError={ordersError}
            kpiOverall={kpiOverall}
            overallAllKinds={basis === 'all'}
            kpiFiltered={kpiFiltered}
            rollingKpis={rollingKpis}
            rollingError={rollingError}
            rollingLoading={rollingLoading}
            kpiLoading={kpiLoading}
            isRefreshing={isRefreshing}
            onRefresh={refreshAllData}
          />
          </Box>

          <FulfillmentTables
            query={query}
            defaultMonths={defaultMonths}
            months={months}
            carrierOptions={carrierOptions}
            countries={countries}
            carrierLoading={carrierLoading}
            countriesLoading={countriesLoading}
            orders={orders}
            ordersTotal={ordersTotal}
            ordersLoading={ordersLoading}
            ordersError={ordersError}
            ordersReady={ordersReady}
            allocRows={allocRows}
            isRefreshing={isRefreshing}
            allocLoading={allocLoading}
            onRefreshAll={refreshAllData}
            onRetryOrders={wiederholen}
            onRebuildAllocation={handleRebuildAllocation}
          />
        </>
      </PageContainer>

      <ConfirmDialog
        open={rebuildConfirmOpen}
        title="Rebuild allocation table?"
        message="This truncates and recomputes all SKU allocations."
        confirmLabel="Rebuild"
        destructive
        busy={rebuildAllocLoading}
        onConfirm={handleRebuildAllocation}
        onCancel={() => setRebuildConfirmOpen(false)}
      />
    </>
  );
}
